import { z } from 'zod';
import { bloodGroupSchema, rhFactorSchema, patientCategorySchema } from './blood';
import { moneySchema } from './pricing';
export const orderStatusSchema = z.enum([
    'submitted', 'revision_requested', 'approved', 'allocated',
    'issued', 'completed', 'rejected', 'cancelled',
]);
export const allocationStatusSchema = z.enum(['reserved', 'issued', 'released']);
export const ORDER_STATUS_LABELS = {
    submitted: 'Menunggu review',
    revision_requested: 'Perlu revisi',
    approved: 'Disetujui',
    allocated: 'Dialokasikan',
    issued: 'Dikirim',
    completed: 'Diterima RS',
    rejected: 'Ditolak',
    cancelled: 'Dibatalkan',
};
export const ALLOCATION_STATUS_LABELS = {
    reserved: 'Dipesan',
    issued: 'Dikirim',
    released: 'Dilepas',
};
/**
 * Legal order transitions. The server enforces them and the client uses them to decide
 * which buttons appear — one source of truth.
 *
 * `allocated -> approved` is the release path: PMI hands the units back and the order
 * waits for allocation again.
 */
export const ORDER_TRANSITIONS = {
    submitted: ['approved', 'rejected', 'revision_requested', 'cancelled'],
    revision_requested: ['submitted', 'cancelled'],
    approved: ['allocated', 'rejected', 'cancelled'],
    // Cancellable: the bags are set aside but have not shipped, so cancelling simply
    // returns them to stock. Forcing a phone call instead would leave them reserved.
    allocated: ['issued', 'approved', 'cancelled'],
    issued: ['completed'],
    completed: [],
    rejected: [],
    cancelled: [],
};
export function canTransitionOrder(from, to) {
    return ORDER_TRANSITIONS[from].includes(to);
}
/** Statuses in which the hospital owes for the bags allocated to it. */
export const BILLABLE_ORDER_STATUSES = ['allocated', 'issued', 'completed'];
/** Statuses a hospital may still withdraw from. */
export const HOSPITAL_CANCELLABLE_STATUSES = ['submitted', 'revision_requested', 'approved', 'allocated'];
// ───────────────────────────── input ─────────────────────────────
export const orderLineInputSchema = z.object({
    componentTypeId: z.string().uuid(),
    abo: bloodGroupSchema,
    rh: rhFactorSchema,
    quantity: z.coerce.number().int().min(1, 'Jumlah minimal 1 kantong').max(200),
});
export const orderInputSchema = z.object({
    patientCategory: patientCategorySchema,
    patientReference: z.string().trim().max(100).optional().nullable(),
    /** Official request letter in S3. Required before review can proceed. */
    requestLetterUrl: z.string().trim().max(1000).optional().nullable(),
    neededBy: z.string().datetime({ offset: true }).optional().nullable(),
    note: z.string().trim().max(1000).optional().nullable(),
    lines: z.array(orderLineInputSchema).min(1, 'Pesanan harus memuat minimal satu komponen'),
}).superRefine((v, ctx) => {
    const keys = v.lines.map((l) => `${l.componentTypeId}|${l.abo}|${l.rh}`);
    const duplicates = keys.filter((k, i) => keys.indexOf(k) !== i);
    if (duplicates.length > 0) {
        ctx.addIssue({
            code: 'custom', path: ['lines'],
            message: 'Ada baris pesanan yang sama (komponen dan golongan darah). Gabungkan jumlahnya.',
        });
    }
});
export const orderReviewSchema = z.object({
    decision: z.enum(['approved', 'rejected', 'revision_requested']),
    note: z.string().trim().max(1000).optional().nullable(),
}).superRefine((v, ctx) => {
    if (v.decision !== 'approved' && !v.note) {
        ctx.addIssue({
            code: 'custom', path: ['note'],
            message: 'Alasan wajib diisi untuk penolakan maupun permintaan revisi.',
        });
    }
});
/**
 * Allocation request.
 *
 * `confirmSubstitutions` is what turns a non-identical blood type from a refusal into an
 * allocation. Without it the server refuses and reports which substitution would be
 * required — the application matches inventory, it does not make the clinical call.
 */
export const allocationInputSchema = z.object({
    /** Restrict to one branch. Omit to allocate across every branch the actor may use. */
    branchId: z.string().uuid().optional().nullable(),
    confirmSubstitutions: z.boolean().default(false),
    substitutionReason: z.string().trim().max(500).optional().nullable(),
}).superRefine((v, ctx) => {
    if (v.confirmSubstitutions && !v.substitutionReason) {
        ctx.addIssue({
            code: 'custom', path: ['substitutionReason'],
            message: 'Alasan substitusi wajib diisi dan akan tercatat di jejak audit.',
        });
    }
});
export const orderCancelSchema = z.object({
    reason: z.string().trim().min(3, 'Alasan pembatalan wajib diisi').max(500),
});
export const orderListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    status: orderStatusSchema.optional(),
    hospitalId: z.string().uuid().optional(),
});
